import { getFragmentData } from 'gql'
import { ErrorFragmentFragmentDoc } from 'gql/graphql'
import { useSnackbar } from 'notistack'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DefaultsContext } from 'features/globalSetup/pages/Defaults/DefaultsContext'
import { mapGraphQLErrors } from 'providers/tanstack-query/errors'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { toDTO } from './api/mapper'
import { UPDATE_WORKSPACE_GENERAL_CONFIGURATION } from './api/mutation'

import type { Schema } from '../../schema'
import type { UseUpdateWorkspaceGeneralConfiguration } from './types'
import type {
  UpdateWorkspaceGeneralConfigurationMutation,
  UpdateWorkspaceGeneralConfigurationMutationVariables,
} from 'gql/graphql'
import type { FieldErrors } from 'react-hook-form'

export const useUpdateWorkspaceGeneralConfiguration =
  (): UseUpdateWorkspaceGeneralConfiguration => {
    const { t } = useTranslation('features/globalSetup', {
      keyPrefix: 'defaults.general.action',
    })
    const { enqueueSnackbar } = useSnackbar()
    const { setLoading } = useContext(DefaultsContext)
    const [errors, setErrors] = useState<FieldErrors<Schema>>({})

    const { isPending, mutateAsync } = useMutation<
      { input: UpdateWorkspaceGeneralConfigurationMutationVariables['input'] },
      UpdateWorkspaceGeneralConfigurationMutation
    >({
      mutation: UPDATE_WORKSPACE_GENERAL_CONFIGURATION,
      onCompleted: async (data: UpdateWorkspaceGeneralConfigurationMutation) => {
        const apiErrors = data.updateWorkspaceGeneralConfiguration?.errors

        if (apiErrors && apiErrors.length > 0) {
          setErrors(mapGraphQLErrors(getFragmentData(ErrorFragmentFragmentDoc, apiErrors)))
          enqueueSnackbar(t('error'), { variant: 'error' })
        } else {
          setErrors({})
          enqueueSnackbar(t('success'), { variant: 'success' })
        }
      },
      queriesToInvalidate: ['getWorkspaceGeneralConfiguration'],
    })

    useEffect(() => {
      setLoading(isPending)
    }, [isPending])

    const update = (data: Schema): void => {
      void mutateAsync({ input: toDTO(data) })
    }

    return {
      errors,
      update,
    }
  }
